/**
 * The board people dialog's decisions that are not geometry (P-043, round 9q).
 *
 * `BoardSubscribersDialog.tsx` is markup and data wiring; the four rules below are the ones a
 * reviewer has to be able to check without a browser, so they live here and are covered by
 * `subscribers.test.ts`. The geometry itself is hit-tested on a served build by
 * `tools/browser/verify-chrome-local.mjs` (round 4's rule: a box is not a render).
 */

/** monday's placeholder, verbatim — 38 characters in the capture's scrubbed input. */
export const SEARCH_PLACEHOLDER = 'Search by name, team, or email address';

/** monday's tooltip on both row buttons of the **last** owner, verbatim (`[c:board-invite]`). */
export const LAST_OWNER_TOOLTIP = 'Choose a new owner first';

/** F3's own address test, so the dialog and the service agree on what an address is. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isAddress(value: string): boolean {
  return EMAIL_RE.test(value.trim());
}

/**
 * *"Only invited people can find this board"*, the 20 px row under the search.
 *
 * The capture has it on a `private` board only (q470). A `share` board is reached the same way
 * — by subscription — so it gets the same sentence; a `public` (Main) board is findable by the
 * whole account, where the sentence would simply be false, so it gets none. That is the
 * conservative reading of a case we did not photograph, not a guess at what monday prints.
 */
export function showsFindabilityHint(kind: string): boolean {
  return kind === 'private' || kind === 'share';
}

/**
 * The account role an address invited **from this board** is created with.
 *
 * F8 §5.5: `share` is the only board kind a guest can ever reach, and 215 of this account's 241
 * users are guests — the shareable board is where monday's guests come from. Inviting a guest
 * from a `public` or `private` board would create somebody who cannot open the board they were
 * invited to, so those invite a member (q471).
 */
export function inviteRoleFor(kind: string): 'member' | 'guest' {
  return kind === 'share' ? 'guest' : 'member';
}

/**
 * Who sees the crown, the × and the invite row (F8 §5.5 `board.subscribers.manage` /
 * `board.owners.manage`): an **owner** of this board, or an account admin — our `superadmin`
 * included. Everybody else reads the list, and the search still filters it.
 *
 * This decides what is *rendered*. `boards.subscribers.*` decides what is *allowed*, and it
 * checks again on every call — a hidden control is not a permission check.
 */
export function canManageSubscribers(
  me: { id: string; role: string } | null,
  members: readonly { id: string; role: string }[],
): boolean {
  if (me === null) return false;
  if (me.role === 'admin' || me.role === 'superadmin') return true;
  return members.some((member) => member.id === String(me.id) && member.role === 'owner');
}

/**
 * *People invited to this board*: owners first, then subscribers, alphabetical inside each.
 * Everyone whose role is `workspace` is **not** on the board — they are reached through the
 * search instead, which is what replaced the old *"In this workspace"* list.
 */
export function orderSubscribers<T extends { name: string; role: string }>(
  members: readonly T[],
): T[] {
  const rank = (role: string): number => (role === 'owner' ? 0 : 1);
  return members
    .filter((member) => member.role === 'owner' || member.role === 'subscriber')
    .slice()
    .sort((a, b) => rank(a.role) - rank(b.role) || a.name.localeCompare(b.name));
}
